import { useState, useEffect } from 'react';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import '../../order/order-list.scss';
import { Typography } from '../../../components/typography/typography';
import { DataTable, DataTableRow, DataTableColumn } from '../../../components/table/data-table';
import { Button } from '../../../components/form/button';
import Icon from '../../../components/icons/icon';
import { DatePicker } from '../../../components/date-picker/date-picker';
import { DateTime } from 'luxon';
import FileSaver from 'file-saver';
import { QuipliLink } from '../../../components/link/quipli-link';
import '../transactions/transaction-report.scss';
import { useSearchParams } from 'react-router-dom';
import useRequestQueryParams from '../../../hooks/use-request-query-params';
import {
  DateTimeRange,
  DeliveryRevenueReportRequest,
  DeliveryRevenueReportRow,
} from '../../../clients/quipli-service/report/report';
import { useReportApiV2 } from '../../../clients/quipli-service/report/report-api-v2';
import { convertToMoney } from '../../../util/util';
import { Location } from '../../../clients/quipli-service/location/location';
import { SelectForm } from '../../../components/select-form/select-form';
import { Card } from '../../../components/card/card';
import { toast } from '../../../util/toasts';
import { SpinnerModal } from '../../../components/modal/spinner-modal';

export const DeliveryRevenueReport = () => {
  const { listLocations, getMultipleConfigurations } = useAPI();
  const { deliveryRevenueReport, exportDeliveryRevenueReport, deliveryRevenueSummary, getAvailableOrdersReportRange } =
    useReportApiV2();

  const [isResumeLoading, setIsResumeLoading] = useState<boolean>(true);
  const [isTableDataLoading, setIsTableDataLoading] = useState<boolean>(true);
  const [tableData, setTableData] = useState<DataTableRow[]>([]);
  const [summary, setSummary] = useState<{ location: string; revenue: string }[]>([]);

  const [availableTaxReportRange, setAvailableTaxReportRange] = useState<DateTimeRange>();
  const [timezone, setTimezone] = useState<string>();
  const [currency, setCurrency] = useState<Dinero.Currency>();
  const [locations, setLocations] = useState<Location[]>([]);
  const [isExporting, setIsExporting] = useState<boolean>(false);

  // List searchRequest hooks
  const [params] = useSearchParams();
  const { request, setRequest } = useRequestQueryParams<DeliveryRevenueReportRequest>(
    {
      from: params.has('from') ? DateTime.fromISO(params.get('from')!) : undefined,
      to: params.has('to') ? DateTime.fromISO(params.get('to')!) : undefined,
      locationId: params.has('locationId') ? parseInt(params.get('locationId')!) : undefined,
    },
    (_request) => {
      setIsTableDataLoading(true);
      setIsResumeLoading(true);

      if (!currency || !timezone || !_request.from || !_request.to) {
        return;
      }

      deliveryRevenueSummary({
        from: _request.from,
        to: _request.to,
        locationId: _request.locationId,
      })
        .then(setSummary)
        .catch(() => {})
        .finally(() => setIsResumeLoading(false));
      deliveryRevenueReport(_request)
        .then(({ items }) => {
          setTableData(items.map((item) => buildDataRow(item)));
          setIsTableDataLoading(false);
        })
        .catch(() => {
          setIsTableDataLoading(false);
          setTableData([]);
        });
    },
    [currency, timezone],
  );

  const columns: DataTableColumn[] = [
    { key: 'InvoiceNumber', label: 'Invoice No.' },
    { key: 'Location', label: 'Location' },
    { key: 'CustomerName', label: 'Customer' },
    { key: 'DueDate', label: 'Due Date' },
    { key: 'Date', label: 'Date' },
    { key: 'Total', label: 'Total' },
  ];

  const buildDataRow = (row: DeliveryRevenueReportRow) => {
    return {
      mobileHeader: (
        <>
          <Typography type="text-md" weight="medium">
            {row['Customer Name']}
          </Typography>
          <Typography type="text-sm" weight="medium">
            <Typography type="text-sm" weight="regular" className="inline text-[var(--grey-500)]">
              Inv. NO{' '}
            </Typography>
            <QuipliLink
              to={`orders/view/${row['Order ID']}`}
              className="underline underline-offset-1 text-[var(--quipli-black-800)]"
            >
              {row['Invoice Number']}
            </QuipliLink>
          </Typography>
        </>
      ),
      fields: {
        InvoiceNumber: (
          <Typography type="text-sm" weight="regular">
            <QuipliLink
              to={`orders/view/${row['Order ID']}`}
              className="underline underline-offset-1 text-[var(--quipli-black-800)]"
            >
              {row['Invoice Number']}
            </QuipliLink>
          </Typography>
        ),
        CustomerName: (
          <Typography type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
            <QuipliLink
              to={`users/customer_form/${row['Customer ID']}`}
              className="underline underline-offset-1 text-[var(--quipli-black-800)]"
            >
              {row['Customer Name']}
            </QuipliLink>
          </Typography>
        ),
        Location: (
          <Typography type="text-sm" weight="regular" className="text-quipli-black-800">
            {row.Location}
          </Typography>
        ),
        DueDate: row['Due Date'] && row['Due Time'] && (
          <div>
            <Typography type="text-sm" weight="regular" className="text-quipli-black-800">
              {DateTime.fromISO(row['Due Date']).toFormat('LLL-dd')}
            </Typography>
            <Typography type="text-sm" weight="regular" className="text-grey-500">
              {row['Due Time']}
            </Typography>
          </div>
        ),
        Date: row.Date && row.Time && (
          <div>
            <Typography type="text-sm" weight="regular" className="text-quipli-black-800">
              {DateTime.fromISO(row['Date']).toFormat('LLL-dd')}
            </Typography>
            <Typography type="text-sm" weight="regular" className="text-grey-500">
              {row['Time']}
            </Typography>
          </div>
        ),
        Total: (
          <Typography type="text-sm" weight="bold" className="text-[var(--quipli-black-800)]">
            {convertToMoney(row.Total, currency!).toFormat()}
          </Typography>
        ),
      },
    };
  };

  const exportToCSV = async () => {
    setIsExporting(true);
    try {
      const file = await exportDeliveryRevenueReport(request);

      const exportFileName = `Delivery_Revenue_Report_${(request.from || DateTime.local())
        .toFormat('D')
        .replaceAll('/', '-')}`;
      const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
      const fileExtension = '.xlsx';

      const data = new Blob([file], { type: fileType });
      FileSaver.saveAs(data, `${exportFileName}${fileExtension}`);
    } catch (e) {
      console.error(e);
      toast('Failed to export delivery revenue', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    const fetchConfigs = async () => {
      const [reportRange, conf, locations] = await Promise.all([
        getAvailableOrdersReportRange(),
        getMultipleConfigurations(['CONF_TIMEZONE', 'CONF_CURRENCY']),
        listLocations(true),
      ]);

      const timezone = conf.find((c) => c.variable === 'CONF_TIMEZONE')?.value;
      const currency = conf.find((c) => c.variable === 'CONF_CURRENCY')?.value;

      setAvailableTaxReportRange(reportRange);
      setTimezone(timezone);
      setCurrency(currency as Dinero.Currency);
      setLocations(locations);

      if (!request.from && !request.to) {
        setRequest({
          from: DateTime.local({ zone: timezone }).startOf('month'),
          to: DateTime.local({ zone: timezone }).endOf('month'),
        });
      }
    };
    fetchConfigs();
  }, []);

  const reportingMonths: DateTimeRange[] = [];
  if (availableTaxReportRange) {
    let currentStart = availableTaxReportRange.from.startOf('month');
    while (currentStart.diffNow().milliseconds < 0) {
      reportingMonths.push({ from: currentStart, to: currentStart.endOf('month') });
      currentStart = currentStart.plus({ months: 1 }).setZone(timezone, { keepLocalTime: true });
    }
  }
  reportingMonths.reverse();
  let selectedMonth = -1;
  if (request.from && request.to) {
    selectedMonth = reportingMonths.findIndex(
      (m) => m.from.diff(request.from!).milliseconds === 0 && m.to.diff(request.to!).milliseconds === 0,
    );
  }

  return (
    <div className="order-list container-v2">
      <div className="flex justify-between items-center">
        <Typography type="display-sm" weight="medium">
          Delivery Revenue
        </Typography>
      </div>

      {/* Filters */}
      <div className="pt-10 pb-4 flex flex-col md:flex-row flex-wrap gap-4 md:items-end md:justify-between">
        <div className="flex flex-col md:flex-row flex-wrap justify-start items-end gap-4">
          <select
            className="h-fit text-[14px] w-full md:w-fit"
            value={selectedMonth}
            required={false}
            onChange={(e) => {
              const range = reportingMonths[parseInt(e.target.value)];
              setRequest((prev) => ({
                ...prev,
                from: range.from,
                to: range.to,
              }));
            }}
          >
            {selectedMonth < 0 && <option value={-1}></option>}
            {reportingMonths.map((m, i) => (
              <option key={i} value={i}>
                {m.from.toFormat('LLLL yyyy')}
              </option>
            ))}
          </select>
          <div className="w-full md:w-fit">
            <DatePicker
              label="From"
              labelType="text-md"
              labelWeight="medium"
              value={request.from}
              onChange={(from) => setRequest((prevState) => ({ ...prevState, from: from?.startOf('day') }))}
              clearable
            />
          </div>
          <div className="w-full md:w-fit">
            <DatePicker
              label="To"
              labelType="text-md"
              labelWeight="medium"
              value={request.to}
              onChange={(to) => setRequest((prevState) => ({ ...prevState, to: to?.endOf('day') }))}
              clearable
            />
          </div>
          <div className="w-full md:w-fit">
            <SelectForm
              label="Location"
              labelType="text-md"
              labelWeight="medium"
              isClearable
              value={locations.find((l) => l.id === request.locationId)}
              options={locations}
              getOptionLabel={(location) => location.name}
              getOptionValue={(location) => `${location.id}`}
              onChange={(location) => setRequest((prevState) => ({ ...prevState, locationId: location?.id }))}
            />
          </div>
        </div>

        <Button className="button-yellow font-semibold h-fit" onClick={exportToCSV} data-cy="export">
          Export
          <Icon icon="Export" size="medium" stroke="var(--grey-700)" />
        </Button>
      </div>

      {/* Summary */}
      <div className="flex w-full flex-wrap mb-3">
        {isResumeLoading ? (
          <Card className="animate-pulse bg-slate-200 m-[2px] p-[12px] min-h-[64px] rounded-md w-full" />
        ) : (
          summary.map((s, i) => (
            <Card
              key={i}
              className="bg-white m-[2px] p-[12px] min-h-[64px] rounded-md flex flex-col flex-[0_1_calc(50%-4px)] lg:flex-[0_1_calc(20%-4px)] gap-2"
            >
              <Typography type="text-sm" weight="regular" className="text-[var(--grey-500)]">
                {s.location}
              </Typography>
              <Typography type="text-sm" weight="medium" className="text-[var(--quipli-black-800)]">
                {s.revenue}
              </Typography>
            </Card>
          ))
        )}
      </div>

      <DataTable
        isLoading={isTableDataLoading}
        columnNames={columns}
        data={tableData}
        request={request}
        setRequest={setRequest}
        cypressPrevious="previous_transactions_page"
        cypressNext="next_transactions_page"
      />
      <SpinnerModal isOpen={isExporting} contentDescription="Exporting Delivery Revenue" />
    </div>
  );
};
